import React, { useState, useEffect } from "react";

import { ModalApp } from "components/Modal";
import Button from "components/Button";
import Radio from "components/CheckboxCustom";
import { classes, initial_passengers as passengers } from 'utils'
import { useTranslation } from "contexts/Safra/TranslationContext";
import { ContentRoom } from "./styles";
import {
    trackOccupantsOpen,
    trackOccupantsChange,
    trackOccupantsComplete
} from "../../../../utils/analytics";
import Icon from 'components/Safra/Icon';

export default function Passengers({ 
    boxed = false,
    initialAdults = 1,
    initialChildren = 0,
    initialBaby = 0,
    initialClass = 'economy'
}) {
    const { t } = useTranslation();
    const [openPassageiro, setOpenPassageiro] = useState(false)
    const [openClasse, setOpenClasse] = useState(false)

    const parseIntOrDefault = (value, fallback) => {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const [adults, setAdults] = useState(initialAdults)
    const [children, setChildren] = useState(initialChildren)
    const [baby, setBaby] = useState(initialBaby)

    const [passageiros, setPassageiros] = useState('1')

    const [selectedClassName, setSelectedClassName] = useState(initialClass);
    const [classe, setClasse] = useState(() => {
        const foundClass = classes.find(c => c.name === initialClass);
        return foundClass ? t(`safra.common.classes.${foundClass.name}`) : t('safra.common.classes.economy');
    });

    // inicializa contagens a partir das props, mantendo defaults mínimos
    useEffect(() => {
        setAdults(initialAdults);
        setChildren(initialChildren);
        setBaby(initialBaby);
        setSelectedClassName(initialClass);
        const foundClass = classes.find(c => c.name === initialClass);
        setClasse(foundClass ? t(`safra.common.classes.${foundClass.name}`) : t('safra.common.classes.economy'));

        if (!localStorage.getItem('cabin')) {
            localStorage.setItem('cabin', 'economy');
        }
        if (!localStorage.getItem('class')) {
            localStorage.setItem('class', 'economy');
        }
        if (!localStorage.getItem('radio-class')) {
            localStorage.setItem('radio-class', 'economy');
        }
    }, [initialAdults, initialChildren, initialBaby, initialClass, t]);

    const handleModalPassageiro = () => {
        const willOpen = !openPassageiro;
        setOpenPassageiro(willOpen);

        // Dispara evento GA4 quando modal de passageiros é aberto
        if (willOpen) {
            trackOccupantsOpen('flight');
        }
    };

    const handleModalClasse = () => {
        setOpenClasse(!openClasse);
    };

    const formatPassageiros = (adultsCount, childrenCount, babyCount) => {
        const total = adultsCount + childrenCount + babyCount;
        setPassageiros(String(total));
    }

    // sincroniza localStorage sempre que contagens mudam, inclusive zerando
    useEffect(() => {
        const normalizedAdults = Math.max(1, adults);
        const normalizedChildren = Math.max(0, children);
        const normalizedBaby = Math.min(Math.max(0, baby), normalizedAdults);

        localStorage.setItem('adults', String(normalizedAdults));
        localStorage.setItem('children', String(normalizedChildren));
        localStorage.setItem('baby', String(normalizedBaby));

        formatPassageiros(normalizedAdults, normalizedChildren, normalizedBaby);
    }, [adults, children, baby]);

    const handlePeople = (type, action) => {

        if (action === 'add') {
            switch (type) {
                case 'adults':
                    if (adults + children + baby < 9) {
                        const newValue = adults + 1;
                        setAdults(newValue);
                        trackOccupantsChange('flight', 'adult', newValue, 'increase');
                    }
                    break;
                case 'children':
                    if (adults + children + baby < 9) {
                        const newValue = children + 1;
                        setChildren(newValue);
                        trackOccupantsChange('flight', 'child', newValue, 'increase');
                    }
                    break;
                case 'baby':
                    if (baby < adults && adults + children + baby < 9) {
                        const newValue = baby + 1;
                        setBaby(newValue);
                        trackOccupantsChange('flight', 'infant', newValue, 'increase');
                    }
                    break;
            }
        } else if (action === 'remove') {

            switch (type) {
                case 'adults':
                    if (adults > 1) {
                        const newValue = adults - 1;
                        setAdults(newValue);
                        trackOccupantsChange('flight', 'adult', newValue, 'decrease');
                        if (baby > adults - 1) {
                            setBaby(baby - 1);
                        }
                    }
                    break;
                case 'children':
                    if (children > 0) {
                        const newValue = children - 1;
                        setChildren(newValue);
                        trackOccupantsChange('flight', 'child', newValue, 'decrease');
                    }
                    break;
                case 'baby':
                    if (baby > 0) {
                        const newValue = baby - 1;
                        setBaby(newValue);
                        trackOccupantsChange('flight', 'infant', newValue, 'decrease');
                    }
                    break;
            }
        }
    }

    const InputControl = ({ type, placeholder, subtitle, value = null }) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--title-color)' }}>{placeholder}</span>
                    <span style={{ fontSize: '14px', fontWeight: 400, color: 'var(--text-color)' }}>{subtitle}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span
                        role={"button"}
                        onClick={() => handlePeople(type, 'remove')}
                        tabIndex={0}
                        aria-label={`Remover um ${placeholder.toLowerCase()}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handlePeople(type, 'remove');
                            }
                        }}
                        style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            border: '1px solid var(--primary-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon name="minus" size={12} color="var(--primary-color)" />
                    </span>
                    <span style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-color)' }} aria-live="polite" aria-atomic="true">{value}</span>
                    <span
                        role={"button"}
                        onClick={() => handlePeople(type, 'add')}
                        tabIndex={0}
                        aria-label={`Adicionar um ${placeholder.toLowerCase()}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handlePeople(type, 'add');
                            }
                        }}
                        style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            border: '1px solid var(--primary-color)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon name="plus" size={12} color="var(--primary-color)" />
                    </span>
                </div>
            </div>
        )
    }

    const PassengerIcon = () => <Icon name="user" size={20} color="black" />;
    const ClassIcon = () => <Icon name="star" size={20} color="black" />;


    function handleClasse(item) {
        // show the Portuguese label in the input
        setClasse(t(`safra.common.classes.${item.name}`));
        setSelectedClassName(item.name);

        // store the internal/class code (English) in localStorage for integrations
        localStorage.setItem('cabin', item.name);
        localStorage.setItem('class', item.name);
        localStorage.setItem('radio-class', item.name);
    }

    const isBoxed = boxed;

    return (
        <div className="content-custom" style={isBoxed ? { gap: 0 } : undefined}>
            {isBoxed ? (
                <div style={{ display: 'flex', width: '100%' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            minHeight: '62px',
                            padding: '8px 16px',
                            flex: 1,
                            border: '1px solid #D8D8D8',
                            borderRight: '0',
                            borderRadius: '4px 0 0 4px',
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px', minHeight: '20px' }}>
                            <PassengerIcon />
                        </span>
                        <div className="d-flex flex-column" style={{ gap: '4px', flex: 1 }}>
                            <span style={{ fontFamily: 'var(--font-family-base)', fontSize: '12px', fontWeight: 400, color: 'var(--text-color)', lineHeight: '100%' }}>{t('safra.components.passengers.title')}</span>
                            <input
                                className="form-control form-control-custom border-0 p-0"
                                value={passageiros}
                                onClick={handleModalPassageiro}
                                id="passPassengers"
                                placeholder=""
                                readOnly
                                aria-label={`${t('safra.components.passengers.title')}: ${passageiros} ${passageiros > 1 ? t('safra.components.search_bar.travellers') : t('safra.components.search_bar.traveller')}`}
                                aria-expanded={openPassageiro}
                                aria-haspopup="dialog"
                                style={{ fontFamily: 'var(--font-family-base)', fontSize: '16px', fontWeight: 600, color: 'var(--text-color)', height: '20px', lineHeight: '100%', boxShadow: 'none', background: 'transparent', cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            minHeight: '62px',
                            padding: '8px 16px',
                            flex: 1,
                            border: '1px solid #D8D8D8',
                            borderRadius: '0 4px 4px 0',
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '20px', minHeight: '20px' }}>
                            <ClassIcon />
                        </span>
                        <div className="d-flex flex-column" style={{ gap: '4px', flex: 1 }}>
                            <span style={{ fontFamily: 'var(--font-family-base)', fontSize: '12px', fontWeight: 400, color: 'var(--text-color)', lineHeight: '100%' }}>{t('safra.components.passengers.class')}</span>
                            <input
                                className="form-control form-control-custom border-0 p-0"
                                value={classe}
                                onClick={handleModalClasse}
                                id="passClass"
                                placeholder=""
                                readOnly
                                aria-label={`${t('safra.components.passengers.class')}: ${classe}`}
                                aria-expanded={openClasse}
                                aria-haspopup="dialog"
                                style={{ fontFamily: 'var(--font-family-base)', fontSize: '14px', fontWeight: 600, color: 'var(--text-color)', height: '20px', lineHeight: '100%', boxShadow: 'none', background: 'transparent', cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col">
                        <div className="d-flex align-items-center" style={{ borderBottom: '1px solid var(--text-color)', paddingBottom: '8px', gap: '12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', minWidth: '20px', minHeight: '20px' }}>
                                <PassengerIcon />
                            </span>
                            <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                <span style={{ fontFamily: 'var(--font-family-base)', fontSize: '12px', color: 'var(--text-color)', letterSpacing: '-0.8px' }}>{t('safra.components.passengers.title')}</span>
                                <input
                                    className="form-control form-control-custom border-0 p-0"
                                    value={passageiros}
                                    onClick={handleModalPassageiro}
                                    id="passPassengers"
                                    placeholder=""
                                    readOnly
                                    aria-label={`${t('safra.components.passengers.title')}: ${passageiros} ${passageiros > 1 ? t('safra.components.search_bar.travellers') : t('safra.components.search_bar.traveller')}`}
                                    aria-expanded={openPassageiro}
                                    aria-haspopup="dialog"
                                    style={{ fontFamily: 'var(--font-family-base)', fontSize: '14px', fontWeight: 600, color: 'var(--text-color)', height: '20px', boxShadow: 'none', background: 'transparent' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col">
                        <div className="d-flex align-items-center" style={{ borderBottom: '1px solid var(--text-color)', paddingBottom: '8px', gap: '12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', minWidth: '20px', minHeight: '20px' }}>
                                <ClassIcon />
                            </span>
                            <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                <span style={{ fontFamily: 'var(--font-family-base)', fontSize: '12px', color: 'var(--text-color)', letterSpacing: '-0.8px' }}>{t('safra.components.passengers.class')}</span>
                                <input
                                    className="form-control form-control-custom border-0 p-0"
                                    value={classe}
                                    onClick={handleModalClasse}
                                    id="passClass"
                                    placeholder=""
                                    readOnly
                                    aria-label={`${t('safra.components.passengers.class')}: ${classe}`}
                                    aria-expanded={openClasse}
                                    aria-haspopup="dialog"
                                    style={{ fontFamily: 'var(--font-family-base)', fontSize: '14px', fontWeight: 600, color: 'var(--text-color)', height: '20px', boxShadow: 'none', background: 'transparent' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ModalApp
                show={openPassageiro}
                handleModal={handleModalPassageiro}
                label={t('safra.components.passengers.title')}
                subtitle={t('safra.components.passengers.select_limit', { count: 9 })}
                customHeader={true}
            >
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <InputControl placeholder={t('safra.components.passengers.adults.label')} subtitle={t('safra.components.passengers.adults.subtitle')} type="adults" value={adults} />
                    <InputControl placeholder={t('safra.components.passengers.children.label')} subtitle={t('safra.components.passengers.children.subtitle')} type="children" value={children} />
                    <InputControl placeholder={t('safra.components.passengers.infants.label')} subtitle={t('safra.components.passengers.infants.subtitle')} type="baby" value={baby} />
                </div>
                <div className="mt-4 pb-4">
                    <Button
                        type="button"
                        width="200px"
                        onClick={() => {
                            // Dispara evento GA4 quando passageiros são confirmados
                            trackOccupantsComplete('flight', adults, children, baby, adults + children + baby);
                            handleModalPassageiro();
                        }}
                        aria-label={t('safra.components.passengers.confirm_selection')}
                    >
                        {t('safra.common.continue')}
                    </Button>
                </div>
            </ModalApp>

            <ModalApp show={openClasse} handleModal={handleModalClasse} label={t('safra.components.passengers.class')}>
                <div className="p-4 bg-content">
                    <div className="row">
                        <div className="col d-grid gap-4">
                            <div className="d-grid gap-2">
                                {classes.map((item, index) =>
                                    <div className="" key={item.name}>
                                        <div className="input-group">
                                            <span className="input-group-custom border-0">
                                                <input
                                                    id={`classe_${item.name}`}
                                                    name="classe"
                                                    type="radio"
                                                    value={item.name}
                                                    checked={selectedClassName === item.name}
                                                    onChange={() => handleClasse(item)}
                                                    aria-label={t(`safra.common.classes.${item.name}`)}
                                                />
                                            </span>
                                            <label className="label-custom color-custom border-0" htmlFor={`classe_${item.name}`}>{t(`safra.common.classes.${item.name}`)}</label>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                <div className="mt-4 pb-2">
                    <Button type="button" width="200px" onClick={handleModalClasse} aria-label={t('safra.components.passengers.confirm_class')}>{t('safra.common.continue')}</Button>
                </div>
            </ModalApp>
        </div>
    );
}
